import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Shield, Smartphone, KeyRound, Loader2, Check, AlertCircle,
  Plus, Trash2, Pencil, X, Copy, ShieldAlert, History, MapPin, Globe,
  FileText, ArrowUpRight, Compass,
} from "lucide-react";

/**
 * "My Account" — self-service security settings.
 *
 * Two-factor authentication (Phase 30.24):
 *   • List enrolled authenticator devices (rename / remove)
 *   • Add another device (password step-up → QR → verify)
 *   • Recovery codes (status, regenerate, view once)
 */
export default function MyAccount() {
  const { user } = useAuth();
  return (
    <div className="space-y-8" data-testid="my-account-page">
      <div>
        <div className="hw-eyebrow mb-2">Account & security</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">My Account</h1>
        <p className="text-[#334155] mt-2 max-w-2xl">
          Settings for <strong>{user?.email}</strong>. To change your name, role, or contact info,
          ask an admin to update your team-member profile.
        </p>
      </div>

      <MfaSection />
      <LandingPageSection />
      <MyNotesThisWeekSection />
      <MyTeamNotesThisWeekSection />
      <SignInLogSection />
      <MyFieldCheckinsSection />
    </div>
  );
}

/* ============================================================ */
/* "My team's notes this week" widget (Supervisors only)         */
/* Read-only sibling to the personal weekly notes widget.        */
/* Hides itself entirely when the caller has no direct reports.  */
/* ============================================================ */

function MyTeamNotesThisWeekSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/notes/team/this-week")
      .then(({ data }) => { if (mounted) setData(data); })
      .catch((e) => {
        if (!mounted) return;
        // Soft-fail — a non-supervisor will see {team_size:0} so the
        // section just hides. Real errors still surface as toasts.
        if (e?.response?.status && e.response.status !== 403) {
          toast.error(formatApiError(e));
        }
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const fmtRelative = (iso) => {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return iso;
    const diffMs = Date.now() - t;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  // Hide entirely for users without direct reports (most staff).
  if (loading) return null;
  if (!data || (data.team_size ?? 0) === 0) return null;

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 lg:p-8 space-y-4" data-testid="team-notes-week-section">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#1E3A8A] text-white flex items-center justify-center flex-shrink-0">
          <FileText className="w-6 h-6" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-xl font-semibold text-[#0F172A]">My team&apos;s notes this week</h2>
          <p className="text-sm text-[#334155] mt-1">
            Read-only snapshot of wellness notes your direct reports logged in the last 7 days. Useful for weekly 1:1 prep and engagement check-ins.
          </p>
        </div>
        <div
          className="hidden sm:block text-right flex-shrink-0"
          data-testid="team-notes-week-count"
        >
          <div className="text-3xl font-display font-semibold text-[#0F172A]">
            {data.count}
          </div>
          <div className="text-xs text-[#1E3A8A]">
            from {data.team_size} report{data.team_size === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {data.notes.length === 0 ? (
        <div className="py-6 text-center text-sm text-[#1E3A8A]" data-testid="team-notes-week-empty">
          Your team hasn&apos;t logged any wellness notes in the last 7 days.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="team-notes-week-list">
          {data.notes.map((n) => {
            const csName = n.care_seeker?.name || "Care Seeker";
            const csId = n.care_seeker?.id;
            const body = n.emotional_wellness_notes || n.notes || "";
            const authorName = n.author?.name || "Team member";
            return (
              <li key={n.id} data-testid={`team-notes-week-row-${n.id}`}>
                <Link
                  to={csId ? `/care-seekers/${csId}` : "#"}
                  className="block bg-[#F9F8F6] hover:bg-white border border-[#E5E5E0] hover:border-[#1E3A8A]/30 rounded-xl px-4 py-3 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider text-white bg-[#1E3A8A] px-2 py-0.5 rounded-full">
                          {authorName}
                        </span>
                        <span className="font-medium text-[#0F172A] truncate">{csName}</span>
                        {n.care_seeker?.display_id && (
                          <span className="font-mono text-[10px] uppercase tracking-wider text-[#1E3A8A] bg-[#E0E7FF] px-1.5 py-0.5 rounded-full">
                            {n.care_seeker.display_id}
                          </span>
                        )}
                        {n.interaction_type && (
                          <span className="text-[10px] uppercase tracking-wider text-[#334155] capitalize">
                            · {n.interaction_type.replace(/_/g, " ")}
                          </span>
                        )}
                      </div>
                      {body && (
                        <p className="text-sm text-[#334155] mt-1 line-clamp-2">{body}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-[#1E3A8A] flex-shrink-0">
                      {fmtRelative(n.created_at)}
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ============================================================ */
/* "Notes I've written this week" widget                         */
/* Per product owner — quick personal reference for navigators   */
/* that mirrors the activity now hidden behind care seeker       */
/* profiles. Read-only; clicking a row opens the parent profile. */
/* ============================================================ */

function MyNotesThisWeekSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/notes/mine/this-week")
      .then(({ data }) => { if (mounted) setData(data); })
      .catch((e) => { if (mounted) toast.error(formatApiError(e)); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const fmtRelative = (iso) => {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return iso;
    const diffMs = Date.now() - t;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 lg:p-8 space-y-4" data-testid="my-notes-week-section">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#7E937D] text-white flex items-center justify-center flex-shrink-0">
          <FileText className="w-6 h-6" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-xl font-semibold text-[#0F172A]">Notes I&apos;ve written this week</h2>
          <p className="text-sm text-[#334155] mt-1">
            Quick reference of wellness notes you logged in the last 7 days.
            Useful for billing, supervision check-ins, and your weekly recap.
          </p>
        </div>
        <div
          className="hidden sm:block text-right flex-shrink-0"
          data-testid="my-notes-week-count"
        >
          <div className="text-3xl font-display font-semibold text-[#0F172A]">
            {data?.count ?? 0}
          </div>
          <div className="text-xs text-[#1E3A8A]">
            note{(data?.count ?? 0) === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-16 rounded-xl bg-[#F9F8F6] animate-pulse" />
          ))}
        </div>
      ) : (data?.notes || []).length === 0 ? (
        <div className="py-6 text-center text-sm text-[#1E3A8A]" data-testid="my-notes-week-empty">
          No notes logged in the last 7 days. When you write one, it&apos;ll appear here.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="my-notes-week-list">
          {data.notes.map((n) => {
            const csName = n.care_seeker?.name || "Care Seeker";
            const csId = n.care_seeker?.id;
            const body = n.emotional_wellness_notes || n.notes || "";
            return (
              <li key={n.id} data-testid={`my-notes-week-row-${n.id}`}>
                <Link
                  to={csId ? `/care-seekers/${csId}` : "#"}
                  className="block bg-[#F9F8F6] hover:bg-white border border-[#E5E5E0] hover:border-[#1E3A8A]/30 rounded-xl px-4 py-3 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-[#0F172A] truncate">{csName}</span>
                        {n.care_seeker?.display_id && (
                          <span className="font-mono text-[10px] uppercase tracking-wider text-[#1E3A8A] bg-[#E0E7FF] px-1.5 py-0.5 rounded-full">
                            {n.care_seeker.display_id}
                          </span>
                        )}
                        {n.interaction_type && (
                          <span className="text-[10px] uppercase tracking-wider text-[#334155] capitalize">
                            · {n.interaction_type.replace(/_/g, " ")}
                          </span>
                        )}
                      </div>
                      {body && (
                        <p className="text-sm text-[#334155] mt-1 line-clamp-2">{body}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-[#1E3A8A] flex-shrink-0">
                      {fmtRelative(n.created_at)}
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ============================================================ */
/* Default Landing Page (Iter 76)                                */
/* Lets a user pick which page loads immediately after sign-in.  */
/* Role-restricted choices are filtered server-side.             */
/* ============================================================ */

function LandingPageSection() {
  const { refresh } = useAuth();
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/me/preferences/landing-page");
      setState(data);
      setSelected(data?.current_key || data?.role_default?.key || "");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.put("/me/preferences/landing-page",
                                     { default_landing_page: selected });
      toast.success("Landing page updated.");
      setState((prev) => ({ ...(prev || {}), current_key: selected, resolved: data.resolved }));
      // Refresh auth context so navbar/redirects immediately use the new route
      try { await refresh(); } catch (_) { /* soft-fail */ }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!state) {
    return (
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 lg:p-8" data-testid="landing-page-section-loading">
        <div className="flex items-center gap-3 text-[#334155]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading preferences…
        </div>
      </div>
    );
  }

  const resolvedRoute = state.resolved?.route || "/";
  const resolvedSource = state.resolved?.source || "role";
  const isDirty = (state.current_key || "") !== selected;

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 lg:p-8 space-y-5" data-testid="landing-page-section">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#1E3A8A] text-white flex items-center justify-center flex-shrink-0">
          <Compass className="w-6 h-6" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-xl font-semibold text-[#0F172A]">
            Default landing page
          </h2>
          <p className="text-sm text-[#334155] mt-1">
            Choose the page that opens automatically when you sign in. Your role default is{" "}
            <strong>
              {state.choices.find((c) => c.key === state.role_default?.key)?.label
                || state.role_default?.key || "Home Dashboard"}
            </strong>.
          </p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {state.choices.map((c) => (
          <label
            key={c.key}
            data-testid={`landing-choice-${c.key}`}
            className={`flex items-start gap-3 border rounded-xl p-4 cursor-pointer transition-colors ${
              selected === c.key
                ? "border-[#1E3A8A] bg-[#F1F5F9]"
                : "border-[#E5E5E0] hover:bg-[#F9F8F6]"
            }`}
          >
            <input
              type="radio"
              name="default_landing_page"
              value={c.key}
              checked={selected === c.key}
              onChange={() => setSelected(c.key)}
              className="mt-1 accent-[#1E3A8A]"
              data-testid={`landing-radio-${c.key}`}
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[#0F172A]">{c.label}</div>
              <div className="text-xs text-[#7E937D] mt-0.5 truncate">
                {c.route || "Uses the last page you visited"}
              </div>
            </div>
          </label>
        ))}
      </div>

      <div className="flex items-center justify-between gap-4 pt-2 border-t border-[#E5E5E0]">
        <div className="text-xs text-[#7E937D]" data-testid="landing-current-preview">
          Currently redirects to <code className="text-[#1E3A8A]">{resolvedRoute}</code>{" "}
          <span className="opacity-70">({resolvedSource})</span>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving || !isDirty}
          data-testid="landing-save-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-60 transition-colors"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          Save preference
        </button>
      </div>
    </div>
  );
}


/* ============================================================ */
/* Two-Factor Authentication section                             */
/* ============================================================ */

function MfaSection() {
  const { user } = useAuth();
  // HC-EPIC-002 · Slice 2C — voluntary MFA-management surfaces are
  // restricted to the canonical `admin` role key. Regular users retain
  // mandatory enrollment + login challenge (via Login.jsx / MfaPanel)
  // and see only the minimum MFA enabled/disabled status here. No
  // device metadata, no recovery-code inventory, no controls. Uses the
  // exact role KEY — no display-name, `system` flag, or role-name
  // bypass.
  const isAdmin = user?.role === "admin";
  const [devices, setDevices] = useState([]);
  const [recovery, setRecovery] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!isAdmin) {
      setDevices([]);
      setRecovery(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [d, r] = await Promise.all([
        api.get("/auth/mfa/devices").catch(() => ({ data: [] })),
        api.get("/auth/mfa/recovery-codes/status").catch(() => ({ data: null })),
      ]);
      setDevices(d.data || []);
      setRecovery(r.data || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [isAdmin]);

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 lg:p-8 space-y-6" data-testid="mfa-section">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#1E3A8A] text-white flex items-center justify-center flex-shrink-0">
          <Shield className="w-6 h-6" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-xl font-semibold text-[#0F172A]">Two-factor authentication</h2>
          <p className="text-sm text-[#334155] mt-1">
            Status:{" "}
            {user?.mfa_enabled ? (
              <span className="text-[#166534] font-medium" data-testid="mfa-status-enabled">
                <Check className="inline w-3.5 h-3.5 mb-0.5 mr-0.5" /> Enabled
              </span>
            ) : (
              <span className="text-[#92400E] font-medium" data-testid="mfa-status-disabled">
                Not yet set up — you&apos;ll be prompted to enroll on next sign-in
              </span>
            )}
          </p>
          {!isAdmin && (
            <p className="text-xs text-[#334155] mt-2" data-testid="mfa-regular-user-note">
              Changes to authenticator devices and recovery codes are handled by a HarborWell administrator.
              Contact your administrator if you need to update or reset your authenticator.
            </p>
          )}
        </div>
      </div>

      {isAdmin && (
        <>
          <DevicesPanel devices={devices} loading={loading} onChanged={load} />

          <hr className="border-[#E5E5E0]" />

          <RecoveryCodesPanel status={recovery} onChanged={load} />
        </>
      )}
    </div>
  );
}

/* ----------- Devices list + add + rename + remove ----------- */

function DevicesPanel({ devices, loading, onChanged }) {
  const [showAdd, setShowAdd] = useState(false);

  return (
    <div data-testid="mfa-devices-panel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="font-display text-base font-semibold text-[#0F172A]">Authenticator apps</h3>
          <p className="text-xs text-[#334155]">
            Microsoft Authenticator, Google Authenticator, Authy, 1Password — any TOTP app works.
          </p>
        </div>
        {!showAdd && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            data-testid="mfa-add-device-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554]"
          >
            <Plus className="w-3.5 h-3.5" /> Add Authenticator App
          </button>
        )}
      </div>

      {showAdd && (
        <AddDeviceFlow onDone={() => { setShowAdd(false); onChanged(); }} onCancel={() => setShowAdd(false)} />
      )}

      {loading ? (
        <div className="bg-[#F9F8F6] rounded-xl h-24 animate-pulse" />
      ) : devices.length === 0 ? (
        <div className="bg-[#F9F8F6] rounded-xl p-6 text-center text-sm text-[#334155]" data-testid="mfa-devices-empty">
          <Smartphone className="w-8 h-8 mx-auto mb-2 text-[#1E3A8A] opacity-50" />
          No authenticator devices yet. Click &quot;Add Authenticator App&quot; to enroll one.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="mfa-devices-list">
          {devices.map((d) => (
            <DeviceRow key={d.id} device={d} onChanged={onChanged} />
          ))}
        </ul>
      )}
    </div>
  );
}

function DeviceRow({ device, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(device.name);
  const [removing, setRemoving] = useState(false);

  const saveName = async () => {
    if (!name.trim() || name === device.name) { setEditing(false); return; }
    try {
      await api.patch(`/auth/mfa/devices/${device.id}`, { name: name.trim() });
      toast.success("Device renamed");
      setEditing(false);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <li
      data-testid={`mfa-device-${device.id}`}
      className="flex items-center justify-between gap-3 px-4 py-3 bg-[#F9F8F6] rounded-xl"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Smartphone className="w-4 h-4 text-[#1E3A8A] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          {editing ? (
            <input
              type="text"
              value={name}
              autoFocus
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => e.key === "Enter" && saveName()}
              data-testid={`mfa-device-rename-input-${device.id}`}
              className="w-full px-2 py-1 rounded-md border border-[#E5E5E0] text-sm bg-white"
            />
          ) : (
            <div className="font-medium text-sm text-[#0F172A] truncate" data-testid={`mfa-device-name-${device.id}`}>
              {device.name}
            </div>
          )}
          <div className="text-xs text-[#1E3A8A]">
            Enrolled {formatShortDate(device.enrolled_at)}
            {device.last_used_at && ` · Last used ${formatShortDate(device.last_used_at)}`}
            {!device.last_used_at && " · Never used"}
          </div>
        </div>
      </div>
      <div className="flex gap-1 flex-shrink-0">
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            title="Rename"
            data-testid={`mfa-device-rename-${device.id}`}
            className="p-1.5 rounded-md text-[#334155] hover:bg-white"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setRemoving(true)}
          title="Remove"
          data-testid={`mfa-device-remove-${device.id}`}
          className="p-1.5 rounded-md text-[#991B1B] hover:bg-white"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {removing && (
        <RemoveDeviceModal
          device={device}
          onCancel={() => setRemoving(false)}
          onDone={() => { setRemoving(false); onChanged(); }}
        />
      )}
    </li>
  );
}

function RemoveDeviceModal({ device, onCancel, onDone }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.delete(`/auth/mfa/devices/${device.id}`, { data: { password } });
      if (data.mfa_disabled) {
        toast.success("Device removed. MFA was disabled — you'll re-enroll on next sign-in.");
      } else {
        toast.success(`Device "${device.name}" removed`);
      }
      onDone();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#0F172A]/40 z-50 flex items-center justify-center p-4" data-testid="mfa-remove-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-[#DC2626]" />
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">Remove this device?</h3>
        </div>
        <p className="text-sm text-[#334155]">
          Removing <strong>{device.name}</strong> will stop accepting codes from that authenticator immediately.
          Confirm your password to continue.
        </p>
        <input
          type="password"
          required
          autoComplete="current-password"
          placeholder="Current password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="mfa-remove-password-input"
          className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
        />
        {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel}
            data-testid="mfa-remove-cancel"
            className="px-4 py-2 rounded-full border border-[#E5E5E0] text-[#334155] text-sm hover:bg-[#F9F8F6]">
            Cancel
          </button>
          <button type="submit" disabled={busy}
            data-testid="mfa-remove-confirm"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#DC2626] text-white text-sm font-medium hover:bg-[#991B1B] disabled:opacity-60">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Remove device
          </button>
        </div>
      </form>
    </div>
  );
}

/* ----------- Add device (password → QR → verify) ----------- */

function AddDeviceFlow({ onDone, onCancel }) {
  const [step, setStep] = useState("password"); // password | scan
  const [password, setPassword] = useState("");
  const [stepupToken, setStepupToken] = useState(null);
  const [enroll, setEnroll] = useState(null);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/mfa/reenroll/password", { password });
      const { data: enrollData } = await api.post("/auth/mfa/devices/begin", { stepup_token: data.stepup_token });
      setStepupToken(data.stepup_token);
      setEnroll(enrollData);
      setStep("scan");
      setPassword("");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/mfa/devices/verify", {
        stepup_token: stepupToken,
        code: code.trim(),
        name: name.trim() || null,
      });
      toast.success("Authenticator added");
      onDone();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#1E3A8A]/30 bg-[#F0F4FF] rounded-2xl p-5 mb-4 space-y-4" data-testid="mfa-add-device-flow">
      {step === "password" && (
        <form onSubmit={submitPassword} className="space-y-3 max-w-md">
          <div className="bg-white border border-[#FCD34D] rounded-xl px-4 py-3 text-xs text-[#92400E] flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            Confirm your current password to add a new authenticator.
          </div>
          <input
            type="password"
            required
            autoComplete="current-password"
            placeholder="Current password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="mfa-add-password-input"
            className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
          />
          {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
          <div className="flex gap-2">
            <button type="submit" disabled={busy}
              data-testid="mfa-add-password-submit"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-60">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Continue
            </button>
            <button type="button" onClick={onCancel}
              data-testid="mfa-add-cancel"
              className="px-5 py-2 rounded-full border border-[#E5E5E0] text-[#334155] text-sm hover:bg-white">
              Cancel
            </button>
          </div>
        </form>
      )}

      {step === "scan" && enroll && (
        <div className="grid md:grid-cols-[200px_1fr] gap-5 items-start">
          <div className="bg-white border border-[#E5E5E0] rounded-xl p-3 inline-block">
            <img src={enroll.qr_png_data_url} alt="QR" className="w-44 h-44" data-testid="mfa-add-qr" />
          </div>
          <form onSubmit={submitCode} className="space-y-3">
            <div>
              <h4 className="font-display text-base font-semibold text-[#0F172A] mb-1">Scan with your authenticator</h4>
              <ol className="text-xs text-[#334155] space-y-0.5 list-decimal pl-4">
                <li>Open Microsoft Authenticator (or any TOTP app).</li>
                <li>Tap the <strong>+</strong> icon → <strong>Other account</strong>.</li>
                <li>Scan the QR code, then enter the 6-digit code below.</li>
              </ol>
              <details className="text-xs text-[#1E3A8A] mt-2">
                <summary className="cursor-pointer hover:underline">Can&apos;t scan? Enter the key manually</summary>
                <code data-testid="mfa-add-manual-secret"
                  className="block mt-2 px-3 py-2 bg-white rounded-md font-mono text-[11px] break-all border border-[#E5E5E0]">
                  {enroll.manual_secret}
                </code>
              </details>
            </div>
            <input
              type="text"
              placeholder={`Device name (optional)`}
              maxLength={80}
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="mfa-add-name-input"
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0] text-sm"
            />
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              data-testid="mfa-add-code-input"
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0] text-center text-lg font-mono tracking-[0.4em]"
              placeholder="000000"
            />
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
            <div className="flex gap-2">
              <button type="submit" disabled={busy || code.length !== 6}
                data-testid="mfa-add-verify-submit"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-60">
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Add device
              </button>
              <button type="button" onClick={onCancel}
                data-testid="mfa-add-verify-cancel"
                className="px-5 py-2 rounded-full border border-[#E5E5E0] text-[#334155] text-sm hover:bg-white">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

/* ----------- Recovery codes ----------- */

function RecoveryCodesPanel({ status, onChanged }) {
  const [showRegen, setShowRegen] = useState(false);
  const [codes, setCodes] = useState(null); // shown ONCE after generate

  return (
    <div data-testid="mfa-recovery-panel">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <h3 className="font-display text-base font-semibold text-[#0F172A]">Recovery codes</h3>
          <p className="text-xs text-[#334155]">
            One-time codes for signing in if you lose access to all your authenticator devices.
          </p>
        </div>
        {!showRegen && (
          <button
            type="button"
            onClick={() => setShowRegen(true)}
            data-testid="mfa-recovery-generate-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#1E3A8A] text-[#1E3A8A] text-sm font-medium hover:bg-[#E0E7FF]"
          >
            <KeyRound className="w-3.5 h-3.5" />
            {status?.total > 0 ? "Regenerate codes" : "Generate recovery codes"}
          </button>
        )}
      </div>

      {status && status.total > 0 && (
        <div className="text-xs text-[#1E3A8A]" data-testid="mfa-recovery-status">
          <strong>{status.count_remaining}</strong> of {status.total} codes remaining
          {status.generated_at && ` · last generated ${formatShortDate(status.generated_at)}`}
        </div>
      )}

      {showRegen && !codes && (
        <RegenerateCodesFlow
          onCancel={() => setShowRegen(false)}
          onCodes={(c) => { setCodes(c); onChanged(); }}
        />
      )}

      {codes && (
        <CodesDisplay
          codes={codes}
          onAcknowledge={() => { setCodes(null); setShowRegen(false); }}
        />
      )}
    </div>
  );
}

function RegenerateCodesFlow({ onCancel, onCodes }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/mfa/reenroll/password", { password });
      const { data: gen } = await api.post("/auth/mfa/recovery-codes/generate", { stepup_token: data.stepup_token });
      toast.success("Recovery codes generated — save them now");
      onCodes(gen.codes);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-3 border border-[#1E3A8A]/30 bg-[#F0F4FF] rounded-2xl p-5 space-y-3 max-w-md" data-testid="mfa-recovery-confirm">
      <div className="bg-white border border-[#FCD34D] rounded-xl px-4 py-3 text-xs text-[#92400E] flex items-start gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        Generating new codes will invalidate any previous recovery codes. Confirm your password to continue.
      </div>
      <input
        type="password"
        required
        autoComplete="current-password"
        placeholder="Current password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        data-testid="mfa-recovery-password-input"
        className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
      />
      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
      <div className="flex gap-2">
        <button type="submit" disabled={busy}
          data-testid="mfa-recovery-confirm-submit"
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-60">
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Generate codes
        </button>
        <button type="button" onClick={onCancel}
          data-testid="mfa-recovery-confirm-cancel"
          className="px-5 py-2 rounded-full border border-[#E5E5E0] text-[#334155] text-sm hover:bg-white">
          Cancel
        </button>
      </div>
    </form>
  );
}

function CodesDisplay({ codes, onAcknowledge }) {
  const [acknowledged, setAcknowledged] = useState(false);
  const copyAll = () => {
    navigator.clipboard.writeText(codes.join("\n"));
    toast.success("All codes copied");
  };
  return (
    <div className="mt-3 border border-[#7E937D] bg-[#E8ECE7] rounded-2xl p-5 space-y-3" data-testid="mfa-recovery-codes-display">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-[#166534] text-white flex items-center justify-center">
          <KeyRound className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <h4 className="font-display text-base font-semibold text-[#166534]">Save these codes now</h4>
          <p className="text-xs text-[#334155]">
            Each code works once. Store them somewhere safe — they will <strong>not</strong> be shown again.
          </p>
        </div>
        <button type="button" onClick={copyAll}
          data-testid="mfa-recovery-copy-all"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-xs font-medium text-[#334155] hover:bg-[#F9F8F6]">
          <Copy className="w-3 h-3" /> Copy all
        </button>
      </div>
      <ul className="grid grid-cols-2 gap-2" data-testid="mfa-recovery-codes-list">
        {codes.map((c, i) => (
          <li key={i} data-testid={`mfa-recovery-code-${i}`}
            className="px-3 py-2 bg-white rounded-md font-mono text-sm text-[#0F172A] border border-[#E5E5E0]">
            {c}
          </li>
        ))}
      </ul>
      <label className="flex items-start gap-2 text-sm text-[#334155]">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          data-testid="mfa-recovery-ack"
          className="mt-1"
        />
        I&apos;ve saved these codes somewhere safe.
      </label>
      <button type="button" onClick={onAcknowledge} disabled={!acknowledged}
        data-testid="mfa-recovery-done"
        className="px-5 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-50">
        Done
      </button>
    </div>
  );
}

/* ----------- helpers ----------- */

function formatShortDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

function formatStamp(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

/* ============================================================ */
/* Recent sign-ins                                               */
/* ============================================================ */

function SignInLogSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/me/sign-in-log", { params: { limit: 25 } })
      .then(({ data }) => setRows(data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 lg:p-8 space-y-4" data-testid="signin-log-section">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#1E3A8A] text-white flex items-center justify-center flex-shrink-0">
          <History className="w-6 h-6" strokeWidth={1.75} />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-xl font-semibold text-[#0F172A]">Recent sign-ins</h2>
          <p className="text-sm text-[#334155]">
            The last 25 times your account was used. Spot anything you don&apos;t recognize? Contact an admin.
          </p>
        </div>
      </div>
      {loading ? (
        <div className="bg-[#F9F8F6] rounded-xl h-24 animate-pulse" />
      ) : rows.length === 0 ? (
        <div className="text-sm text-[#334155] bg-[#F9F8F6] rounded-xl p-4" data-testid="signin-log-empty">
          No sign-ins recorded yet.
        </div>
      ) : (
        <ul className="divide-y divide-[#F0EFEB]" data-testid="signin-log-list">
          {rows.map((r) => (
            <li key={r.id} className="py-3 flex items-start justify-between gap-3" data-testid={`signin-row-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#0F172A]">{formatStamp(r.timestamp)}</div>
                <div className="text-xs text-[#334155] flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                  {r.city && <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" />{r.city}{r.region && `, ${r.region}`}</span>}
                  {r.ip && <span className="font-mono">{r.ip}</span>}
                  <span className="text-[10px] uppercase tracking-wider bg-[#E0E7FF] text-[#1E3A8A] px-1.5 py-0.5 rounded-full">
                    {r.mfa_method?.replace("_", " ") || "totp"}
                  </span>
                </div>
                {r.user_agent && (
                  <div className="text-[11px] text-[#94A3B8] truncate mt-0.5" title={r.user_agent}>{r.user_agent}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ============================================================ */
/* My field check-ins                                            */
/* ============================================================ */

function MyFieldCheckinsSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/field/check-ins/me", { params: { limit: 25 } })
      .then(({ data }) => setRows(data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 lg:p-8 space-y-4" data-testid="my-checkins-section">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#7E937D] text-white flex items-center justify-center flex-shrink-0">
          <MapPin className="w-6 h-6" strokeWidth={1.75} />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-xl font-semibold text-[#0F172A]">My field check-ins</h2>
          <p className="text-sm text-[#334155]">
            Your recent visits in the field. Use the <strong>Check in</strong> button in the top header
            when you arrive somewhere, and <strong>Check out</strong> when you leave.
          </p>
        </div>
      </div>
      {loading ? (
        <div className="bg-[#F9F8F6] rounded-xl h-24 animate-pulse" />
      ) : rows.length === 0 ? (
        <div className="text-sm text-[#334155] bg-[#F9F8F6] rounded-xl p-4" data-testid="my-checkins-empty">
          No field check-ins yet.
        </div>
      ) : (
        <ul className="divide-y divide-[#F0EFEB]" data-testid="my-checkins-list">
          {rows.map((r) => {
            const loc = r.manual_label || (r.city ? `${r.city}${r.region ? `, ${r.region}` : ""}` : "Unknown");
            return (
              <li key={r.id} className="py-3" data-testid={`my-checkin-row-${r.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <MapPin className="w-3.5 h-3.5 text-[#1E3A8A]" />
                  <span className="text-sm font-medium text-[#0F172A]">{loc}</span>
                  <span className="text-[10px] uppercase tracking-wider text-[#1E3A8A] bg-[#E0E7FF] px-1.5 py-0.5 rounded-full">
                    {r.status}
                  </span>
                  {r.county && <span className="text-xs text-[#334155]">· {r.county}</span>}
                </div>
                <div className="text-xs text-[#334155] mt-0.5">
                  In {formatStamp(r.check_in_at)}
                  {r.check_out_at && ` · Out ${formatStamp(r.check_out_at)}`}
                </div>
                {r.note && <p className="text-sm text-[#334155] mt-1">{r.note}</p>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
